"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-4 w-14 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-14 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-14 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-14 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group/link relative text-foreground font-mono text-[12px] uppercase tracking-[0.15em] text-foreground/70 hover:text-foreground hover:bg-transparent focus:bg-transparent px-3 py-2 transition-colors duration-200">
                <span className="relative">
                  {item.label}
                  <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-primary transition-all duration-300 group-hover/link:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-8">
        <div className="h-12 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-12 w-2/3 bg-muted animate-pulse rounded-md" />
        <div className="h-12 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-12 w-1/2 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-2 px-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onNavigate} className="text-foreground font-default text-4xl sm:text-5xl font-bold uppercase tracking-tight leading-tight py-3 border-b border-border/30 hover:text-primary transition-colors duration-200">
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="4313" id="main-navigation" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-300 ${scrolled ? "bg-background/95 backdrop-blur-md border-b border-border" : "bg-background border-b border-transparent"}`}>
      <span className="hidden lg:block absolute left-3 top-1/2 -translate-y-1/2 font-mono text-[10px] tracking-[0.2em] text-foreground/40 select-none pointer-events-none" aria-hidden="true">
        [VESTA.ARCH]
      </span>

      <div className={`w-full px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-all duration-300 ${scrolled ? "h-16" : "h-20"}`}>
        <div className="flex items-center pl-0 lg:pl-10">
          <Link to="/" aria-label="VESTA home" className="flex items-center">
            <WvcLogo className="h-7 w-auto" />
          </Link>
        </div>

        <div className="hidden lg:flex items-center justify-center flex-1">
          <MenuProvider menu_id="41" className="header-menu">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden xl:flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-foreground/60">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full bg-primary opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 bg-primary" />
            </span>
            <span>JURISDICTION: ACTIVE</span>
          </div>

          <Button asChild className="hidden sm:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 font-default font-bold uppercase tracking-[0.1em] text-xs px-5 h-10 shadow-md transition-all duration-200">
            <Link to="/ContactPage">Consultation →</Link>
          </Button>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:bg-foreground/10 hover:text-foreground" aria-label="Open menu">
                <Menu className="size-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background text-foreground border-border w-full sm:max-w-md p-0 flex flex-col">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>

              <div className="flex items-center justify-between px-6 pt-6 pb-8 border-b border-border">
                <span className="font-mono text-[10px] tracking-[0.2em] text-foreground/40 uppercase">
                  [VESTA.ARCH] / Menu
                </span>
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-foreground/60">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full bg-primary opacity-75 animate-ping" />
                    <span className="relative inline-flex h-1.5 w-1.5 bg-primary" />
                  </span>
                  <span>Active</span>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto py-10">
                <MenuProvider menu_id="41" className="mobile-header-menu">
                  <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                </MenuProvider>
              </div>

              <div className="p-6 border-t border-border">
                <SheetClose asChild>
                  <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-default font-bold uppercase tracking-[0.1em] text-sm h-14 shadow-md">
                    <Link to="/ContactPage">Consultation →</Link>
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}